<template>
  <div class="bg-grey-lightest">
    <section id="heading" class="pt-12 pb-24">
      <!-- Header Nav -->
      <header-nav></header-nav>
    </section>
    <section class="py-12">
      <div class="max-w-sm mx-auto px-6">
        <div v-if="errorText">
          <div class="rounded bg-white shadow relative -mt-24">
            <router-link
              :to="{ path: '/' }"
              class="text-red-lighter hover:text-red-lightest text-xl absolute pin-t pin-r p-2 m-2"
            >
              <span class="icon">
                <i class="mdi mdi-close"></i>
              </span>
            </router-link>
            <div
              class="flex items-center justify-center bg-red text-white rounded px-4 py-10 w-full"
            >
              <span class="icon text-5xl text-white">
                <i class="mdi mdi-alert-circle"></i>
              </span>
              <span
                class="text-sm text-xl ml-4"
                v-text="errorText ? errorText : $t('an_error_occured')"
              ></span>
            </div>
          </div>
        </div>
        <div v-else>
          <div v-if="appointment">
            <div class="rounded bg-white shadow relative -mt-24">
              <router-link
                :to="{ path: '/' }"
                class="text-whiter hover:text-white text-xl absolute pin-t pin-r p-2 m-2"
              >
                <span class="icon">
                  <i class="mdi mdi-close"></i>
                </span>
              </router-link>
              <div class="p-6 bg-teal text-white rounded-t">
                <div
                  class="inline-flex items-center justify-between bg-transparent uppercase font-semibold text-white text-left text-sm"
                >{{ $t('Appointment_confirmed') }}</div>
                <div
                  class="mt-4 text-sm"
                >{{ $t('The_practitioner_is_allowed_to_ask_for_a_compensation_if_you_dont_show_up_at_the_appointment_') }}</div>
              </div>
              <div class="p-4 md:p-6">
                <div class="mb-4">
                  <div class="text-blue font-semibold text-sm mb-1">{{ $t('When_') }}</div>
                  <div
                    class="text-black font-semibold text-lg"
                  >{{ appointment.date | momentFormat('YYYY-MM-DD', $t('date_selector_format')) }} {{ $t('at') }} {{ appointment.time | momentFormatTime($t('time_format')) }}</div>
                </div>
                <div class="mb-4 flex justify-between">
                  <div>
                    <div
                      class="text-blue font-semibold text-sm mb-1"
                    >{{ $t('Who_will_treat_me_') }}</div>
                    <div
                      class="text-grey-darkest"
                    >{{ appointment.doctorFirstName + ' ' + appointment.doctorLastName }}</div>
                    <div
                      class="text-green font-semibold text-sm mt-1 mb-2"
                      v-if="appointment.isDoctorRegulated && (appointment.emergencyType !== 'OSTEOPATH' && appointment.emergencyType !== 'PSYCHOLOGIST')"
                    >€ {{ $t('Contracted_doctor') }}</div>
                    <div
                      class="text-red font-semibold text-sm mt-1 mb-2"
                      v-if="!appointment.isDoctorRegulated && (appointment.emergencyType !== 'OSTEOPATH' && appointment.emergencyType !== 'PSYCHOLOGIST')"
                    >€ {{ $t('Contracted_doctor_negative') }}</div>
                    <a
                      :href="'tel:' + appointment.officePhone"
                      class="flex md:hidden items-center text-blue outline-0 uppercase tracking-wide"
                    >
                      <span class="icon">
                        <i class="mdi mdi-phone"></i>
                      </span>
                      <span class="ml-2 text-xs font-semibold">{{ $t('Call') }}</span>
                    </a>
                    <button
                      @click.prevent="showOfficePhone"
                      class="hidden md:inline-flex items-center text-blue outline-0 uppercase tracking-wide"
                    >
                      <span class="icon">
                        <i class="mdi mdi-phone"></i>
                      </span>
                      <span class="ml-2 text-xs font-semibold">{{ $t('Call') }}</span>
                    </button>
                  </div>
                  <div>
                    <avatar
                      :avatar="appointment.doctorAvatar"
                      :name="appointment.doctorFirstName + ' ' + appointment.doctorLastName"
                      class="w-10 h-10"
                    ></avatar>
                  </div>
                </div>
                <div>
                  <div
                    class="text-blue font-semibold text-sm mb-1"
                  >{{ $t('Where_have_I_to_go_') }}</div>
                  <div class="text-grey-darkest mb-2">
                    {{ appointment.officeZipCode }} {{ appointment.officeLocality }}
                    <br />
                    {{ appointment.officeStreetNumber }} {{ appointment.officeStreetName }}
                  </div>
                  <a
                    :href="'https://www.google.com/maps/dir/?api=1&destination=' + appointment.officeStreetNumber + ' ' + appointment.officeStreetName + ', ' + appointment.officeZipCode + ' ' + appointment.officeLocality"
                    target="_blank"
                    class="flex md:hidden items-center text-blue outline-0 uppercase tracking-wide"
                  >
                    <span class="icon">
                      <i class="mdi mdi-directions"></i>
                    </span>
                    <span class="ml-2 text-xs font-semibold">{{ $t('Itinerary') }}</span>
                  </a>
                  <a
                    :href="'https://www.google.com/maps/dir/?api=1&destination=' + appointment.officeStreetNumber + ' ' + appointment.officeStreetName + ', ' + appointment.officeZipCode + ' ' + appointment.officeLocality"
                    target="_blank"
                    class="hidden md:inline-flex items-center text-blue outline-0 uppercase tracking-wide"
                  >
                    <span class="icon">
                      <i class="mdi mdi-directions"></i>
                    </span>
                    <span class="ml-2 text-xs font-semibold">{{ $t('Itinerary') }}</span>
                  </a>
                </div>
              </div>
              <gmap-map
                :center="{lat: appointment.officeLocation._lat, lng: appointment.officeLocation._long }"
                :zoom="12"
                :options="{disableDefaultUI: true}"
                style="height: 250px;"
                class="w-full"
              >
                <gmap-marker
                  :position="{lat: appointment.officeLocation._lat, lng: appointment.officeLocation._long }"
                ></gmap-marker>
              </gmap-map>
            </div>
            <div class="text-center mt-3">
              <button
                @click.prevent="showCancel"
                class="text-xs hover:underline text-grey font-semibold uppercase tracking-wide px-1 py-1"
              >{{ $t('Cancel_the_appointment') }}</button>
            </div>
            <dfp-slot
              v-if="appointment"
              path="/21865209613/Patients/AppointmentShow"
              :size="[300,250]"
              :id="`div-gpt-ad-partner-appointment`"
              :medical-practice="appointment.emergencyType"
              class="mt-6"
            />
          </div>
          <div v-else>
            <patient-loading></patient-loading>
          </div>
        </div>
      </div>
    </section>
    <cancel-appointment-modal
      :active="showCancelModal"
      :appointment="appointment"
      @close="hideCancel"
    ></cancel-appointment-modal>
  </div>
</template>

<script>
import firebase from "firebase/app";
import helpers from "@/mixins/helpers";

import HeaderNav from "@/components/Patient/HeaderNav";
import CancelAppointmentModal from "@/components/Patient/CancelAppointmentModal";

import appointmentRepository from "@/data/AppointmentRepository";

export default {
  name: "AppointmentShow",

  mixins: [helpers],

  components: { HeaderNav, CancelAppointmentModal },

  data() {
    return {
      appointment: null,
      showCancelModal: false,
      errorText: null
    };
  },

  mounted() {
    this.fetchAppointment();
  },

  methods: {
    showCancel() {
      this.showCancelModal = true;
    },

    hideCancel() {
      this.showCancelModal = false;
    },

    showOfficePhone() {
      alert(
        this.$t("Call_the_doctor_at_this_number", {
          phoneNumber: this.appointment.officePhone
        })
      );
    },

    fetchAppointment() {
      this.appointmentId = this.$route.params.id;

      appointmentRepository.find(
        this.appointmentId,
        appointment => {
          if (appointment.patientId === this.patient.id) {
            this.appointment = appointment;
          } else {
            this.$router.push("/");
          }

          this.setLoading(false);
        },
        error => {
          this.errorText = error.message;
        }
      );
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/Patient/AppointmentShow.vue